import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-light-svg-icons/faBolt';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../common/types';
import { CreationInferenceBox } from './CreationInferenceBox';
import { CreationSlugField } from './CreationSlugField';
import { CreationThemeRow } from './CreationThemeRow';
import { CreationDateField } from './CreationDateField';
import { CreationDraft } from './props';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface ManualFields {
  title: string;
  artistName: string;
  releaseType: string;
}

interface CreationManualStageProps {
  /** What the artist searched for and we could not find. */
  query: string;
  fields: ManualFields;
  onFieldChange: (field: keyof ManualFields, value: string) => void;
  draft: CreationDraft;
  slugPrefix: string;
  today: string;
  /** The way out: a page that is not about a release at all. */
  onStartBlank: () => void;
}

/**
 * Nothing matched, which for an unreleased track is the normal answer.
 *
 * There is no cover field: the page and the new catalog row start with no cover, and the song page
 * adds one (an upload or the cover editor). A pasted url is gone (design presave-create).
 *
 * So the sheet stops searching and takes the artist's word for it. Everything
 * typed here becomes a catalog row as well as a page, because the Catalog is
 * where a release the artist owns is supposed to live.
 */
const CreationManualStageComponent: React.FC<CreationManualStageProps> = ({
  query,
  fields,
  onFieldChange,
  draft,
  slugPrefix,
  today,
  onStartBlank,
}) => {
  const intl = useIntl();

  // Pre-save is the only page type on offer here — there is nothing to route
  // to. The other button leaves the release behind entirely.
  const typeOptions = useMemo(
    () => [
      {
        key: 'presave',
        label: intl.formatMessage({ id: 'app.sml.create.type.presave.short', defaultMessage: 'Pre-save' }),
        isOn: true,
      },
      {
        key: 'blank',
        label: intl.formatMessage({ id: 'app.sml.create.type.blank.short', defaultMessage: 'Blank' }),
        isOn: false,
        onSelect: onStartBlank,
      },
    ],
    [intl, onStartBlank]
  );

  const onTitle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('title', e.target.value),
    [onFieldChange]
  );
  const onArtist = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('artistName', e.target.value),
    [onFieldChange]
  );
  const onType = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('releaseType', e.target.value),
    [onFieldChange]
  );

  const titleLabel = intl.formatMessage({ id: 'app.sml.create.manual.title', defaultMessage: 'Title' });
  const artistLabel = intl.formatMessage({ id: 'app.sml.create.manual.artist', defaultMessage: 'Artist' });
  const typeLabel = intl.formatMessage({ id: 'app.sml.create.manual.type', defaultMessage: 'Type' });

  return (
    <>
      <div className={styles.note}>
        <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.noteIcon} />
        <span>
          {intl.formatMessage(
            {
              id: 'app.sml.create.manual.no_match',
              defaultMessage:
                'No match for “{query}” on any music service — normal for something that is not out yet. Fill it in and it goes into your catalog too.',
            },
            { query }
          )}
        </span>
      </div>

      <CreationInferenceBox
        type={draft.pageType}
        why={intl.formatMessage({
          id: 'app.sml.create.manual.why',
          defaultMessage: 'Nothing to route to yet, so the page collects instead.',
        })}
        options={typeOptions}
      />

      <div className={styles.fields}>
        <div className={clsx(styles.field, styles.fieldWide)}>
          <span className={styles.fieldLabel}>{titleLabel}</span>
          <div className={styles.fieldBox}>
            <input className={styles.fieldInput} value={fields.title} aria-label={titleLabel} onChange={onTitle} />
          </div>
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{artistLabel}</span>
          <div className={styles.fieldBox}>
            <input
              className={styles.fieldInput}
              value={fields.artistName}
              aria-label={artistLabel}
              onChange={onArtist}
            />
          </div>
          <span className={styles.fieldHelp}>
            {intl.formatMessage({ id: 'app.sml.create.manual.artist.help', defaultMessage: 'From your account' })}
          </span>
        </div>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{typeLabel}</span>
          <div className={styles.fieldBox}>
            <input
              className={styles.fieldInput}
              value={fields.releaseType}
              placeholder="Single"
              aria-label={typeLabel}
              onChange={onType}
            />
          </div>
        </div>

        <CreationDateField value={draft.releaseDate} min={today} onChange={draft.setReleaseDate} />

        <CreationSlugField
          slug={draft.slug}
          prefix={slugPrefix}
          platformDomain={draft.platformDomain}
          onPlatformDomainChange={draft.setPlatformDomain}
        />
      </div>

      <div className={clsx(styles.note)}>
        <FontAwesomeIcon icon={faBolt} className={clsx(styles.noteIcon, styles.noteAccent)} />
        <span>
          {intl.formatMessage({
            id: 'app.sml.create.manual.release_note',
            defaultMessage:
              'When it is out, mark it released from the page’s manage screen. The links attach, every waiting fan is saved, and the page becomes a release page.',
          })}
        </span>
      </div>

      <CreationThemeRow theme={draft.theme} onChange={draft.setTheme} />
    </>
  );
};

export const CreationManualStage = TypedMemo(CreationManualStageComponent);
